<template>
<div class="pending-panel">
    <h1 class="md-title pending-panel-title">Pending Project Invites</h1>

    <div v-if="!userPendingProjectsData" class="pending-loading">
        <svg style="height: 48px;" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><circle fill='#73D5E3' stroke='#73D5E3' stroke-width='15' r='15' cx='40' cy='65'><animate attributeName='cy' calcMode='spline' dur='2' values='65;135;65;' keySplines='.5 0 .5 1;.5 0 .5 1' repeatCount='indefinite' begin='-.4'></animate></circle><circle fill='#73D5E3' stroke='#73D5E3' stroke-width='15' r='15' cx='100' cy='65'><animate attributeName='cy' calcMode='spline' dur='2' values='65;135;65;' keySplines='.5 0 .5 1;.5 0 .5 1' repeatCount='indefinite' begin='-.2'></animate></circle><circle fill='#73D5E3' stroke='#73D5E3' stroke-width='15' r='15' cx='160' cy='65'><animate attributeName='cy' calcMode='spline' dur='2' values='65;135;65;' keySplines='.5 0 .5 1;.5 0 .5 1' repeatCount='indefinite' begin='0'></animate></circle></svg>
        <p>Please wait while we load your invited projects...</p>
    </div>

    <template v-else>
        <p v-if="projectsList.length === 0" class="pending-empty">No pending invites</p>

        <div class="pending-row" v-for="project in projectsList" :key="project.id">
            <span class="pending-cover">
                <img v-if="showPoster(project)" :src="project.posterUrl" :alt="project.title" @error="onPosterError(project)" />
                <span v-else class="pending-generated" :style="{ backgroundColor: colorFor(project) }">{{ initialsFor(project) }}</span>
            </span>
            <span class="pending-meta">
                <span class="pending-title" :title="project.title">{{ project.title }}</span>
                <span class="pending-inviter" v-if="project.inviterName">Invited by {{ project.inviterName }}</span>
            </span>
            <span class="pending-actions">
                <md-button class="md-mini md-fab pending-action-button" @click="accept(project)">
                    <md-icon>person_add</md-icon>
                    <md-tooltip>Join Project</md-tooltip>
                </md-button>
                <md-button class="md-mini md-fab pending-action-button" @click="decline(project)">
                    <md-icon>delete</md-icon>
                    <md-tooltip>Cancel Invite</md-tooltip>
                </md-button>
            </span>
        </div>
    </template>
</div>
</template>

<script>
/**
 * Pending Project Invites panel. Driven by the live-bound userInvitations
 * getter (Firestore listener) so new invites appear with no refresh. Each row
 * shows the project's cover + title and who sent the invite ("Invited by …"),
 * resolved from the invitation's `inviter` uid via the cached ownerLabelByUID.
 */
import { mapGetters } from 'vuex';
import { coverInitials, coverColor, hasPoster } from '@/lib/projectCover';

const PendingProjects = {
    name: 'PendingProjects',
    props: ['user', 'userPendingProjectsData'],
    data: function() {
        return {
            resolvedProjects: [],
            resolveToken: 0,
            failedPosters: {}
        };
    },
    computed: {
        ...mapGetters([
            'userInvitations',
            'projectFromID',
            'ownerLabelByUID'
        ]),
        projectsList() {
            return this.resolvedProjects;
        },
        // Stable signal that changes whenever the set of pending invites changes
        // (userInvitations derives from the live invitations listener).
        invitationKey() {
            return this.userInvitations
                .map(inv => `${inv.pid}:${inv.inviter || ''}`)
                .sort()
                .join(',');
        }
    },
    watch: {
        invitationKey: {
            immediate: true,
            handler: function() {
                this.resolvePendingProjects();
            }
        }
    },
    methods: {
        async accept(project) {
            const userEmail = (this.user.email || '').toLowerCase();
            const pendingCollab = this.userInvitations.find(
                pc =>
                    (pc.email || '').toLowerCase() === userEmail &&
                    project.id === pc.pid
            );
            if (!pendingCollab) {
                console.error(
                    '[noteslist-integration] accept: no pending invitation for',
                    userEmail,
                    'project',
                    project.id
                );
                return;
            }

            const fullProject = await this.projectFromID(project.id);
            if (!fullProject || !fullProject.id) {
                console.error(
                    '[noteslist-integration] accept: projectFromID failed for',
                    project.id
                );
                return;
            }

            await this.$store.dispatch('addCollaborator', {
                project: fullProject,
                uid: this.user.uid,
                pendingCollaborator: pendingCollab
            });

            this.$emit('projectAccepted', project.id);
        },
        decline(project) {
            const pendingCollab = this.userInvitations.find(pc => pc.email === this.user.email && project.id === pc.pid);

            this.$store.dispatch('removePendingCollaborator', {
                project: project,
                pendingCollaboratorID: pendingCollab.id
            });
        },
        initialsFor(project) {
            return coverInitials(project.title);
        },
        colorFor(project) {
            return coverColor(project.id);
        },
        showPoster(project) {
            return hasPoster(project, this.failedPosters[project.id]);
        },
        onPosterError(project) {
            this.$set(this.failedPosters, project.id, true);
        },
        // Resolve each invite's project doc + inviter name. Token-guarded so a
        // slower in-flight resolve can't overwrite a newer one.
        async resolvePendingProjects() {
            const token = ++this.resolveToken;
            const invitations = this.userInvitations;
            try {
                const resolved = await Promise.all(
                    invitations.map(async (inv) => {
                        const project = await this.projectFromID(inv.pid);
                        if (!project) return null;
                        const inviterName = inv.inviter ? await this.ownerLabelByUID(inv.inviter) : '';
                        return { id: inv.pid, ...project, inviterName };
                    })
                );
                if (token !== this.resolveToken) return;
                this.resolvedProjects = resolved.filter(Boolean);
            } catch (error) {
                console.error('Error fetching pending projects:', error);
                if (token === this.resolveToken) this.resolvedProjects = [];
            }
        }
    },
    components: {}
};
export default PendingProjects;
</script>

<style lang="scss">
.pending-panel {
    background: #e9e9e9;
    border-radius: 8px;
    padding: 16px 18px;

    .pending-panel-title {
        margin: 0 0 12px;
        font-size: 20px;
    }
    .pending-loading {
        text-align: center;
        color: #666;
    }
    .pending-empty {
        color: #888;
    }

    .pending-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 0;

        .pending-cover {
            flex: 0 0 auto;
            img, .pending-generated {
                display: block;
                width: 36px; height: 36px;
                border-radius: 4px;
                object-fit: cover;
            }
            .pending-generated {
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: 700;
                font-size: 14px;
            }
        }
        .pending-meta {
            flex: 1 1 auto;
            min-width: 0;
            display: flex;
            flex-direction: column;
            .pending-title {
                font-weight: 600;
                color: #333;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .pending-inviter {
                font-size: 13px;
                color: #888;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
        .pending-actions {
            flex: 0 0 auto;
            display: flex;
            gap: 4px;
        }
        .pending-action-button {
            background: var(--accent) !important;
            &:hover { background: #4dbccb !important; }
        }
    }
}
.md-theme-show-mode {
    .pending-panel {
        background: #2b3940;
        .pending-panel-title { color: rgba(255, 255, 255, 0.9); }
        .pending-row .pending-meta .pending-title { color: rgba(255, 255, 255, 0.92); }
        .pending-row .pending-meta .pending-inviter { color: rgba(255, 255, 255, 0.6); }
    }
}
</style>


// WEBPACK FOOTER //
// pending-projects.vue?dc72b15e